import React, { createContext, useCallback, useEffect, useState } from 'react';
import { FavoritePageFiltersType } from 'pages/FavoritesPage';
import { UserActions } from 'store/User/Actions';
import { useUser } from 'store/User/Context';
import { commonApiRequests } from 'utils/api-requests/common';
import { rentOffersApiRequests } from 'utils/api-requests/rent';
import { v2filtersApiRequests } from 'utils/api-requests/v2filters';
import { createUniqueFilterName } from 'utils/createUniqueFilterName';
import { generateUniqueId } from 'utils/formatters/generateUniqueId';
import { getNormalizedPropertyType } from 'utils/formatters/getNormalizedPropertyType';
import { getValueFromState, initialState, setStateValues } from 'utils/state-managment/rent/rentFilter';
import { rentInitialState, RentOffersResponse, RentStateType } from 'utils/state-managment/rent/rentOffer';
import { AllModuleTypes } from 'utils/types/AllModules';
import { SavedFilter } from 'utils/types/Filter';
import { MultipleFilterType, ObjectAndSingleValueChangeType } from 'utils/types/InputTypes';
import { UserReactions } from 'utils/types/OfferData';
import { RentFilterReturnData, RentFiltersStateType } from 'utils/types/Rent';
import { Module } from 'utils/types/RentModels';
import { AdvertValidatorType, isFieldValid } from 'utils/validators/advertAddingValidator';

import { Note } from 'components/common/NoteBox';
import { activeNotification } from 'components/functions/activeNotification';

interface LoadRentOffersType {
    requestData?: RentFilterReturnData,
    page?: number,
    rows?: number
}

interface RentContextType {
    handleChangePropertyType: (value: Module) => void,
    propertyType: Module,
    rentFiltersState: RentFiltersStateType
    activeFiltersState: RentFiltersStateType
    handleDeleteFilter: (id: number) => void,
    handleSetFiltersValues: (id: number) => void,
    handleFillFilterFields: (FilterData: RentFilterReturnData) => void,
    handleChange: (name: string, value: ObjectAndSingleValueChangeType) => void
    validateField: (name: AdvertValidatorType, value: ObjectAndSingleValueChangeType) => boolean
    clearFilters: (reset?: boolean) => void
    validateAllFields: () => boolean
    getSavedFilters: (id?: number) => void
    handleChangeDateList: (fieldName: string, value: number) => void
    handleActiveFilterChange: (fieldName: string, value: string | number | boolean | MultipleFilterType) => void,
    handleSaveOrUpdateFilter: () => void,
    handleCreateFromExistFilter: () => void,
    handleChangeNotificationAlert: (valueName: string, hashedId: number) => void,
    rentOfferState: RentStateType,
    loadRentOffers: (rentOffersOptions?: LoadRentOffersType) => void;
    loadDuplicates: (type: Module, id: number) => void,
    loadOfferDetailsAsync: (id: number, module: Module) => void,
    loadOpinionsAsync: (id: number, module: Module) => void,
    deleteNote: (id: number, offerType: Module, noteId: number) => void,
    updateNote: (module: Module, noteContent: string, advertisementId: number, noteId: number) => void,
    reactionUpdate: (id: number, city: string, reaction: UserReactions, module: Module) => void,
    clearOffers: () => void,
    loadNotesAsync: (id: string, module: AllModuleTypes, getNotes?: boolean) => Promise<Note[] | void>;
    updateOpinion: (module: Module, commentContent: string, rating: number, advertisementId: number, opinionId?: number) => Promise<void>,
    deleteOpinion: (id: number, module: Module, opinionId: number) => void,
    loadFavoriteOffers: (type: Module[], page?: number, size?: number, filters?: FavoritePageFiltersType, sortType?: string) => void,
    setLoadingPage: () => void,
    stopLoadingPage: () => void,
    loadSimilarOffers: (advertisementId: number, module: Module) => Promise<void>;
}

const values: RentContextType = {
    handleChangePropertyType: () => {},
    handleDeleteFilter: () => {},
    handleSetFiltersValues: () => {},
    handleFillFilterFields: () => {},
    handleChange: () => {},
    handleChangeDateList: () => {},
    clearFilters: () => {},
    validateField: () => false,
    validateAllFields: () => false,
    propertyType: Module.RENT_FLAT,
    rentFiltersState: {} as RentFiltersStateType,
    activeFiltersState: {} as RentFiltersStateType,
    getSavedFilters: () => {},
    handleActiveFilterChange: () => {},
    handleSaveOrUpdateFilter: () => {},
    handleCreateFromExistFilter: () => {},
    handleChangeNotificationAlert: () => {},
    rentOfferState: {} as RentStateType,
    loadRentOffers: () => {},
    loadDuplicates: () => {},
    loadOfferDetailsAsync: () => {},
    loadOpinionsAsync: () => {},
    deleteNote: () => {},
    updateNote: () => {},
    reactionUpdate: () => {},
    clearOffers: () => {},
    loadNotesAsync: () => Promise.resolve(),
    updateOpinion: () => new Promise(() => {}),
    deleteOpinion: () => {},
    loadFavoriteOffers: () => {},
    setLoadingPage: () => {},
    stopLoadingPage: () => {},
    loadSimilarOffers: async () => {}
};

const RentContext = createContext(values);

type MappedIdType = Record<string, { id: number, module: Module}>;

export const RentProvider = ({ children }: { children: React.ReactNode }) => {
    const { dispatch: dispatchUser } = useUser();
    const [propertyType, setPropertyType] = useState<Module>(Module.RENT_FLAT);
    const [rentFiltersState, setRentFiltersState] = useState(initialState.filterInitialState());
    const [activeFiltersState, setActiveFiltersState] = useState(initialState.filterInitialState());
    const [rentOfferState, setRentOfferState] = useState(rentInitialState);
    const [idMap, setIdMap] = useState<MappedIdType>({});

    const setLoadingPage = useCallback(() => {
        setRentOfferState((prev) => ({
            ...prev,
            isLoading: true
        }));
    }, []);

    const stopLoadingPage = useCallback(() => {
        setRentOfferState((prev) => ({
            ...prev,
            isLoading: false
        }));
    }, []);

    const loadRentOffers = async (rentOffersOptions?: LoadRentOffersType) => {
        const { requestData, page, rows } = rentOffersOptions || {};

        setLoadingPage();
        const moduleToUse = requestData?.module ?? rentFiltersState.module;
        const response = await rentOffersApiRequests.getRentOffers(requestData ? requestData : getValueFromState(rentFiltersState), moduleToUse, page ?? 1, rows ?? 25);

        if (requestData && typeof requestData.daysAmount === 'number') {
            setRentFiltersState((prevFilters) => ({
                ...prevFilters,
                daysAmount: requestData.daysAmount as number
            }));
        }

        if (response) {
            setRentOfferState((prev) => ({
                ...prev,
                rentOfferList: response,
                isLoading: false
            }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const loadDuplicates = async (type: Module, id: number) => {
        setLoadingPage();

        const duplicates = await rentOffersApiRequests.getRentOfferDuplicates(type, id);

        setRentOfferState((prev) => ({
            ...prev,
            rentOfferList: {
                ...prev.rentOfferList,
                content: prev.rentOfferList.content.map((el) => {
                    if (el.advertisementId === id) {
                        return {
                            ...el,
                            duplicates: duplicates
                        };
                    }

                    return el;
                })
            },
            isLoading: false
        }));
    };

    const loadOfferDetailsAsync = async (id: number, module: Module) => {
        setLoadingPage();

        const response = await rentOffersApiRequests.getRentOfferDetails(module, id);

        setRentOfferState((prevState) => ({
            ...prevState,
            rentOfferList: {
                ...prevState.rentOfferList,
                content: prevState.rentOfferList.content.map((el) => {
                    if (el.advertisementId === response.advertisementId) {
                        return {
                            ...el,
                            detailedContent: response
                        };
                    }

                    return el;
                })
            },
            isLoading: false,
            notes: response.note
        }));
    };

    const loadOpinionsAsync = async (id: number, module: Module) => {
        const opinions = await rentOffersApiRequests.getOpinions(id, module);

        setRentOfferState((prev) => ({
            ...prev,
            scoreAndOpinions: opinions
        }));
    };

    const clearOffers = () => {
        setRentOfferState(rentInitialState);
        setRentFiltersState((prev) => ({
            ...prev,
            lowerOriginalPrice: false,
            unlockedAdvertisements: false

        }));
    };

    const updateNote = async (module: Module, noteContent: string, advertisementId: number, noteId: number) => {
        setLoadingPage();

        const validChangeStatus = await commonApiRequests.updateNote(module, noteContent, advertisementId, noteId);

        if (validChangeStatus) {
            if (noteId) {
                setRentOfferState((prev) => {
                    return {
                        ...prev,
                        notes: prev.notes!.map((item) => {
                            if (item.noteId === noteId) {
                                return {
                                    ...item,
                                    date: validChangeStatus.date,
                                    content: validChangeStatus.content
                                };
                            }

                            return item;
                        }),
                        rentOfferList: {
                            ...prev.rentOfferList,
                            content: prev.rentOfferList.content.map((x) => {
                                if (x.advertisementId === advertisementId) {
                                    return {
                                        ...x,
                                        note: {
                                            ...validChangeStatus,
                                            noteId: noteId
                                        }
                                    };
                                }

                                return x;
                            })
                        },
                        isLoading: false
                    };
                });
            } else {
                setRentOfferState((prev) => {
                    return {
                        ...prev,
                        notes: prev.notes ? [validChangeStatus].concat(prev.notes) : [validChangeStatus],
                        rentOfferList: {
                            ...prev.rentOfferList,
                            content: prev.rentOfferList.content.map((x) => {
                                if (x.advertisementId === advertisementId) {
                                    return {
                                        ...x,
                                        note: {
                                            ...validChangeStatus
                                        },
                                        numberOfNotes: (x.numberOfNotes ?? 0) + 1
                                    };
                                }

                                return x;
                            })
                        },
                        isLoading: false
                    };
                });
            }
        }
    };

    const updateOpinion = async (module: Module, commentContent: string, rating: number, advertisementId: number, opinionId?: number) => {
        setLoadingPage();

        if (opinionId) {
            const response = await rentOffersApiRequests.updateOpinion(opinionId, commentContent, rating, module);

            if (response) {
                setRentOfferState((prev) => {
                    return {
                        ...prev,
                        rentOfferList: {
                            ...prev.rentOfferList,
                            content: prev.rentOfferList.content.map((offer) => {
                                if (offer.advertisementId === advertisementId) {
                                    return {
                                        ...offer,
                                        ...response
                                    };
                                }

                                return offer;
                            })
                        },
                        isLoading: false
                    };
                });
            }
        } else {
            const response = await rentOffersApiRequests.addOpinion(advertisementId, commentContent, rating, module);
            const newOpinions = await rentOffersApiRequests.getOpinions(advertisementId, module);

            if (response) {
                setRentOfferState((prev) => {
                    return {
                        ...prev,
                        scoreAndOpinions: newOpinions,
                        rentOfferList: {
                            ...prev.rentOfferList,
                            content: prev.rentOfferList.content.map((offer) => {
                                if (offer.advertisementId === advertisementId) {
                                    return {
                                        ...offer,
                                        ...response,
                                        myOpinion: {
                                            id: response.opinionId,
                                            dateAdded: new Date().toDateString(),
                                            numberOfStars: rating,
                                            content: commentContent
                                        }
                                    };
                                }

                                return offer;
                            })
                        },
                        isLoading: false
                    };
                });
            }
        }
    };

    const deleteOpinion = async (id: number, module: Module, opinionId: number) => {
        setLoadingPage();

        const response = await rentOffersApiRequests.deleteOpinion(opinionId, module);

        if (response) {
            setRentOfferState((prev) => {
                return {
                    ...prev,
                    scoreAndOpinions: {
                        ...prev.scoreAndOpinions!,
                        opinions: prev.scoreAndOpinions!.opinions!.filter((item) => item.opinionId !== opinionId)
                    },
                    rentOfferList: {
                        ...prev.rentOfferList,
                        content: prev.rentOfferList.content.map((offer) => {
                            if (offer.advertisementId === id) {
                                return {
                                    ...offer,
                                    ...response,
                                    myOpinion: undefined
                                };
                            }

                            return offer;
                        })
                    },
                    isLoading: false
                };
            });
        }
    };

    const deleteNote = async (id: number, offerType: Module, noteId: number) => {
        setLoadingPage();

        const validChangeStatus = await commonApiRequests.deleteNote(offerType, noteId);

        if (validChangeStatus) {
            setRentOfferState((prev) => {
                return {
                    ...prev,
                    notes: prev.notes!.filter((item) => item.noteId !== noteId),
                    rentOfferList: {
                        ...prev.rentOfferList,
                        content: prev.rentOfferList.content.map((offer) => {
                            if (offer.advertisementId === id) {
                                return {
                                    ...offer,
                                    numberOfNotes: (offer.numberOfNotes ?? 0) - 1
                                };
                            }

                            return offer;
                        })
                    },
                    isLoading: false
                };
            });
        }
    };

    const reactionUpdate = async (id: number, city: string, reaction: UserReactions, module: Module) => {
        const response = await commonApiRequests.setOfferReaction(id, city, reaction, module);
        setRentOfferState((prev) => ({
            ...prev,
            rentOfferList: {
                ...prev.rentOfferList,
                content: prev.rentOfferList.content.map((el) => {
                    if (el.advertisementId === id) {
                        return {
                            ...el,
                            reaction: response
                        };
                    }

                    return {
                        ...el,
                        duplicates: el.duplicates?.map((item) => {
                            if (item.advertisementId === id) {
                                return {
                                    ...item,
                                    reaction: response
                                };
                            }

                            return item;
                        })
                    };
                })
            }
        }));
    };

    const loadNotesAsync = async (id: string, module: AllModuleTypes, getNotes?: boolean) => {
        const notes = await commonApiRequests.getNotes(module, id);

        setRentOfferState((prev) => ({
            ...prev,
            notes
        }));

        if (getNotes) {
            return notes;
        }
    };

    const handleSetFiltersValues = async (id: number, idMapParam?: MappedIdType) => {
        const currentIdMap = idMapParam || idMap;
        const filterId = currentIdMap[id].id;
        const filterModuleType = currentIdMap[id].module;
        const filterData = await rentOffersApiRequests.getFilterDetailsRent(filterId, filterModuleType ? Module[filterModuleType] : propertyType) as RentFilterReturnData;

        if (filterData) {
            setRentFiltersState((filters: RentFiltersStateType) => ({
                ...setStateValues({ ...filterData, id, locations: rentFiltersState.locations }),
                savedFilter: filters.savedFilter
            }));
        } else {
            setRentFiltersState((filters: RentFiltersStateType) => ({
                ...filters,
                name: '',
                id: null
            }));
        }
    };

    const handleFillFilterFields = useCallback((filterData: RentFilterReturnData) => {
        setRentFiltersState((filters: RentFiltersStateType) => {
            const convertedData = setStateValues(filterData);

            return {
                ...initialState.filterInitialState(),
                ...convertedData,
                locations: filters.locations,
                module: convertedData.module || filters.module,
                name: '',
                id: undefined
            };
        });
    }, []);

    const getSavedFilters = useCallback(async (id?: number) => {
        const savedFilters = await rentOffersApiRequests.getSavedFilters();
        const newIdMap: MappedIdType = {};
        const savedFiltersHashedId = savedFilters.map((x) => {
            const hashedId = generateUniqueId(`${x.id}:${x.module}`);
            newIdMap[hashedId] = { id: x.id, module: Module[x.module] };

            return { ...x, id: hashedId };
        });

        setIdMap((prev) => ({
            ...prev,
            ...newIdMap
        }));

        setRentFiltersState((filters: RentFiltersStateType) => ({
            ...filters,
            savedFilter: savedFiltersHashedId as SavedFilter[]
        }));

        if (id) {
            const hashedId = Object.keys(newIdMap).find((x) => newIdMap[x].id === id);

            await handleSetFiltersValues(Number(hashedId), newIdMap);
        }
    }, [propertyType]);

    const handleChangeNotificationAlert = async (valueName: string, hashedId: number) => {
        const currentState = { ...rentFiltersState };
        const newValue = !currentState[valueName as keyof RentFiltersStateType];
        const realFilterId = idMap[hashedId].id;

        const updatedState = {
            ...currentState,
            [valueName]: newValue
        };

        if (valueName === 'alertSms' && newValue === true) {
            updatedState.tossedUp = false;
            handleChange('tossedUp', false);
        }

        handleChange(valueName, newValue);
        const propertyType = getNormalizedPropertyType(rentFiltersState.module);

        await rentOffersApiRequests.updateFilter(getValueFromState({ ...updatedState, id: realFilterId }), propertyType);
        getSavedFilters(realFilterId);
    };

    const handleSaveOrUpdateFilter = async () => {
        if (Object.keys(rentFiltersState.savedLocations).length === 0) {
            activeNotification('Popraw wpisane dane', 'Musisz wybrać poprawną miejscowość', 'warning');

            return;
        }

        const existFilter = rentFiltersState.savedFilter?.find(
            (filter) => filter.id === rentFiltersState.id
        );

        const existName = rentFiltersState.savedFilter?.find(
            (filter) => filter.name === rentFiltersState.name
        );

        const propertyType = getNormalizedPropertyType(rentFiltersState.module);
        let id;

        if (existName && !rentFiltersState.id) {
            activeNotification('Nazwa filtra', 'Istnieje już filtr o takiej nazwie', 'warning');

            return;
        }

        if (existFilter) {
            const id = idMap[existFilter.id].id;

            await rentOffersApiRequests.updateFilter({
                ...getValueFromState({ ...rentFiltersState, id })
            }, propertyType);
        } else {
            const resp = await rentOffersApiRequests.addFilter({
                ...getValueFromState(rentFiltersState)
            }, getNormalizedPropertyType(rentFiltersState.module));

            id = resp?.data;
        }

        getSavedFilters(id);
    };

    const handleCreateFromExistFilter = async () => {
        if (Object.keys(rentFiltersState.savedLocations).length === 0) {
            activeNotification('Popraw wpisane dane', 'Musisz wybrać poprawną miejscowość', 'warning');

            return;
        }

        const newName = createUniqueFilterName(rentFiltersState.name!, rentFiltersState.savedFilter as SavedFilter[]);

        const newFilterData = {
            ...getValueFromState(rentFiltersState),
            name: newName
        };

        const resp = await rentOffersApiRequests.addFilter(newFilterData, getNormalizedPropertyType(rentFiltersState.module));

        const id = resp?.data;

        getSavedFilters(id);
    };

    const clearFilters = useCallback((reset?: boolean) => {
        const initialFilters = initialState.filterInitialState();

        if (reset) {
            setRentFiltersState((prev) => ({
                ...initialFilters,
                module: propertyType,
                locations: prev.locations,
                lowerOriginalPrice: false,
                unlockedAdvertisements: false
            }));
        } else {
            setRentFiltersState((prev) => ({
                ...prev,
                module: propertyType,
                locations: prev.locations,
                city: reset ? [] : prev.city,
                savedLocations: reset ? {} : prev.savedLocations,
                floor: initialFilters.floor,
                floors: initialFilters.floors,
                builtYear: initialFilters.builtYear,
                buildingType: initialFilters.buildingType,
                numberOfRooms: initialFilters.numberOfRooms,
                lowerOriginalPrice: false,
                unlockedAdvertisements: false
            }));
        }
        getSavedFilters();
        UserActions.changeErrorVisibility(dispatchUser, false);
    }, [
        dispatchUser,
        getSavedFilters,
        propertyType
    ]);

    const handleChangePropertyType = useCallback((value: Module) => {
        setPropertyType(value);
        clearFilters();
        setRentFiltersState((prev) => ({
            ...prev,
            id: null
        }));
    }, [clearFilters]);

    const handleDeleteFilter = useCallback(async (filterId: number) => {
        const realFilterId = idMap[filterId].id;
        const filterModuleType = idMap[filterId].module;
        await rentOffersApiRequests.removeSavedFilter(realFilterId, filterModuleType ? filterModuleType : propertyType);
        delete idMap[filterId];

        setRentFiltersState(initialState.filterInitialState());
        getSavedFilters();
    }, [
        rentFiltersState.id,
        rentFiltersState.savedFilter,
        getSavedFilters
    ]);

    const handleChange = useCallback((fieldName: string, value: ObjectAndSingleValueChangeType) => {
        setRentFiltersState((filters: RentFiltersStateType) => {
            const updatedFilters = {
                ...filters,
                [fieldName]: value
            };

            if (fieldName === 'alertSms' && value === true) {
                updatedFilters.tossedUp = false;
            }

            return updatedFilters;
        });
    }, [rentFiltersState]);

    const handleActiveFilterChange = useCallback((fieldName: string, value: string | number | boolean | MultipleFilterType) => {
        setActiveFiltersState((filters: RentFiltersStateType) => ({
            ...filters,
            [fieldName]: value
        }));
    }, []);

    // TODO: Do ogarniecia pod typ rent
    // const baseRequiredFields = ['cityName', 'title', 'area', 'price', 'typeOfMarket', 'propertyType', 'description'];
    // const flatRequiredFields = ['propertyForm'];
    // const houseRequiredFields = ['rentPrice', 'depositPrice', 'propertyForm'];
    // const plotRequiredFields = ['waivePrice'];
    // const otherRequiredFields = ['otherAdType'];
    // const validateAllFields = () => {
    //     switch (propertyType) {
    //     case PropertyType.FLAT:
    //         return [...baseRequiredFields, ...flatRequiredFields].every((item) => isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, newAdvertFields[item as keyof NewAdvertStateType] as string));
    //     case PropertyType.HOUSE:
    //         return [...baseRequiredFields, ...houseRequiredFields].every((item) => isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, newAdvertFields[item as keyof NewAdvertStateType] as string));
    //     case PropertyType.PLOT:
    //         return [...baseRequiredFields, ...plotRequiredFields].every((item) => isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, newAdvertFields[item as keyof NewAdvertStateType] as string));
    //     case PropertyType.OTHER:
    //         return [...baseRequiredFields, ...otherRequiredFields].every((item) => isFieldValid(AdvertValidatorType.NO_EMPTY_TEXT, newAdvertFields[item as keyof NewAdvertStateType] as string));
    //     default:
    //         return false;
    //     }
    // };

    const getVoivodeships = useCallback(async () => {
        const locations = await v2filtersApiRequests.getVoivodeships();
        setRentFiltersState((filters: RentFiltersStateType) => ({
            ...filters,
            locations
        }));
    }, []);

    const handleChangeDateList = (fieldName: string, value: number) => {
        setRentFiltersState((filters: RentFiltersStateType) => ({
            ...filters,
            offerAdded: {
                from: value === -1 ? null : new Date(new Date().setDate(new Date().getDate() - value)).toISOString().split('T')[0],
                to: new Date().toISOString().split('T')[0]
            },
            [fieldName]: value
        }));
    };

    const loadFavoriteOffers = async (type: Module[], page?: number, size?: number, filters?: FavoritePageFiltersType, sortType?: string) => {
        setLoadingPage();

        const response = await rentOffersApiRequests.getRentFavoriteOffers(type, page ?? 1, size ?? 25, filters, sortType) as unknown as RentOffersResponse;

        setRentOfferState((prev) => ({
            ...prev,
            rentOfferList: response,
            isLoading: false
        }));
    };

    const loadSimilarOffers = async (advertisementId: number, module: Module) => {
        const filterData = await rentOffersApiRequests.getSimilarOffersFilter(advertisementId, module);

        if (filterData) {
            handleFillFilterFields(filterData);
            await loadRentOffers({ requestData: filterData, page: 1, rows: 25 });
            document.body.scrollTo(0, 0);
        }
    };

    useEffect(() => {
        clearFilters();
    }, [propertyType]);

    useEffect(() => {
        if (rentFiltersState.locations.length === 0) {
            getVoivodeships();
        }
    }, [rentFiltersState.locations]);

    const providerValues = {
        handleChangePropertyType,
        handleDeleteFilter,
        handleSetFiltersValues,
        handleFillFilterFields,
        handleChange,
        clearFilters,
        validateField: isFieldValid,
        validateAllFields: () => false,
        propertyType: Module.RENT_FLAT,
        rentFiltersState,
        getSavedFilters,
        handleChangeDateList,
        handleActiveFilterChange,
        activeFiltersState,
        handleSaveOrUpdateFilter,
        handleCreateFromExistFilter,
        handleChangeNotificationAlert,
        rentOfferState,
        loadRentOffers,
        loadDuplicates,
        loadOfferDetailsAsync,
        loadOpinionsAsync,
        deleteNote,
        updateNote,
        reactionUpdate,
        clearOffers,
        loadNotesAsync,
        updateOpinion,
        deleteOpinion,
        loadFavoriteOffers,
        setLoadingPage,
        stopLoadingPage,
        loadSimilarOffers
    };

    return (
        <RentContext.Provider value={providerValues}>
            {children}
        </RentContext.Provider>
    );
};

export default RentContext;
